import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import ModalCloseButton from '../components/generic/ModalCloseButton';
import {
  CheckCircleIcon,
  LightningIcon,
  WarningCircleIcon,
  LockSimpleIcon,
  ClockIcon,
  ArrowRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CalendarDotsIcon,
  CrownSimpleIcon,
  FireIcon,
  MedalIcon,
  TrophyIcon,
  AtomIcon,
  FlaskIcon,
  FunctionIcon,
  ExamIcon,
  CheckIcon,
  RocketLaunchIcon,
  SparkleIcon,
  XIcon,
  WarningIcon,
  TimerIcon,
  ListChecksIcon,
  GaugeIcon,
  FootprintsIcon,
  BooksIcon,
  ArrowsClockwiseIcon,
  ScrollIcon,
  ClipboardTextIcon,
  CalendarCheckIcon,
  TargetIcon,
  CalendarStarIcon,
  RankingIcon,
  DiamondIcon,
  CrownIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import { JEE_MAIN_EXAM_ID } from './jeeMainPaywall';
import QuizrrLogoLight from '../../public/Images/QuizrrLogoLight.svg';
import './Challenge99Home.css';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Every day's real calendar date comes from the backend (GET /api/challenge/activity-grid's
// per-day `date` field) - never guessed client-side. `estimatedDate` below is only a last-resort
// fallback for the brief window before that grid has loaded, so the week picker has something to
// render; it's intentionally approximate (assumes the challenge started on a fixed date) and gets
// replaced the instant real data arrives.
const estimatedDate = (dayNo) => {
  const dt = new Date(2026, 9, 14);
  dt.setDate(dt.getDate() + (dayNo - 1));
  return dt;
};
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
// Backend serializes a day's date as the UTC instant for IST midnight (utils/challengeDate.js's
// toISTMidnight) - e.g. "...T18:30:00.000Z" for a day that's 00:00 IST. Every call site below reads
// the parsed Date back with LOCAL getters (.getDate()/.getDay()/.getMonth()), which silently rolls
// back a calendar day on any device whose local timezone is far enough behind IST for that UTC
// instant to fall on the previous local day - correct by accident only on a device near IST. Shift
// into IST first, then read the calendar date off UTC getters and rebuild a plain local-component
// Date (same construction estimatedDate already uses), so it's device-timezone-safe everywhere else
// in this file.
const toIstCalendarDate = (iso) => {
  const shifted = new Date(new Date(iso).getTime() + IST_OFFSET_MS);
  return new Date(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
};
const resolveDate = (dayNo, activityByDay) => {
  const iso = activityByDay?.get(dayNo)?.date;
  return iso ? toIstCalendarDate(iso) : estimatedDate(dayNo);
};
const mondayOffsetOf = (date) => (date.getDay() + 6) % 7; // Monday=0..Sunday=6
const dowLabel = (date) => DOW[date.getDay() === 0 ? 6 : date.getDay() - 1];

function WelcomeMoment({ onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="c99h-mx">
      <div className="c99h-mx-bg" onClick={onClose} />
      <section className="c99h-sh" role="dialog" aria-modal="true" aria-labelledby="c99hShT">
        <button className="c99h-sh-x" onClick={onClose} aria-label="Close" type="button">
          <XIcon weight="bold" />
        </button>
        <div className="c99h-sh-bdg">
          <span className="c99h-sh-rays" />
          <span className="c99h-sh-glow" />
          <SparkleIcon weight="fill" className="c99h-sh-sp s1" />
          <SparkleIcon weight="fill" className="c99h-sh-sp s2" />
          <SparkleIcon weight="fill" className="c99h-sh-sp s3" />
          <SparkleIcon weight="fill" className="c99h-sh-sp s4" />
          <span className="c99h-sh-medal">
            <RocketLaunchIcon weight="fill" />
          </span>
        </div>
        <h2 id="c99hShT">You&rsquo;re in!</h2>
        <p className="c99h-sh-p">
          Your journey starts now. Stay consistent, keep showing up every day, and all the best!
        </p>
        <button className="c99h-sh-go" onClick={onClose} type="button">
          Let&rsquo;s go
          <ArrowRightIcon weight="bold" />
        </button>
      </section>
    </div>
  );
}

// task.label (ChallengeTask, content-authored) and task.subject.icon are the real sources now -
// the subject-title/testType matching below is only a fallback for the shared-dev-DB edge case
// the backend flagged (a task seeded before the `label` field existed won't have it until that
// seed script is re-run). A subject-less task (QFT/PYQ paper/unknown) still gets the same icon
// treatment as the QPT card - one consistent "test" glyph, not a different guess per test type.
const subjectMeta = (task) => {
  const subjectTitle = task.subject?.title?.toLowerCase();
  const iconUrl = task.subject?.icon;
  const kind =
    task.label ||
    (subjectTitle
      ? 'Special PYQ Mock'
      : task.testId?.testType === 'QFT'
        ? 'Full Test'
        : task.testId?.testType === 'PYQ_paper'
          ? 'PYQ as Mock'
          : 'Mock Test');
  const cls = subjectTitle?.includes('phys')
    ? 'physics'
    : subjectTitle?.includes('chem')
      ? 'chemistry'
      : subjectTitle?.includes('math')
        ? 'maths'
        : 'qft';

  if (iconUrl) return { cls, iconUrl, kind };
  if (subjectTitle?.includes('phys')) return { cls, Icon: AtomIcon, kind };
  if (subjectTitle?.includes('chem')) return { cls, Icon: FlaskIcon, kind };
  if (subjectTitle?.includes('math')) return { cls, Icon: FunctionIcon, kind };
  return { cls, Icon: ExamIcon, kind };
};

function FallingBehindSheet({ missedCount, onClose, onViewMissed }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="c99h-mx">
      <div className="c99h-mx-bg" onClick={onClose} />
      <section className="c99h-bs" role="dialog" aria-modal="true" aria-labelledby="c99hBsT">
        <button className="c99h-sh-x" onClick={onClose} aria-label="Close" type="button">
          <XIcon weight="bold" />
        </button>
        <span className="c99h-bs-ic">
          <WarningIcon weight="fill" />
        </span>
        <span className="c99h-sh-k">This week</span>
        <h2 id="c99hBsT">You&rsquo;re falling behind</h2>
        <p className="c99h-sh-p">
          You&rsquo;ve missed {missedCount} mock{missedCount === 1 ? '' : 's'} this week. Catch up to keep earning XP
          toward your weekly rank.
        </p>
        <div className="c99h-bs-ft">
          <button className="c99h-sh-go" onClick={onViewMissed} type="button">
            Catch up now
            <ArrowRightIcon weight="bold" />
          </button>
          <button className="c99h-bs-no" onClick={onClose} type="button">
            Not now
          </button>
        </div>
      </section>
    </div>
  );
}

function PromoBanner({ banner }) {
  if (!banner) return null;
  return (
    <div className="c99h-promo">
      <img src={banner.mobileBanner || banner.banner} alt={banner.title} />
    </div>
  );
}

function ChallengeBanner({ dayNo, weekNo }) {
  return (
    <header className="c99h-dh">
      <div className="c99h-dh-l">
        <div className="c99h-dh-br">
          <span className="c99h-dh-lg">
            <img src={QuizrrLogoLight} alt="Quizrr" />
          </span>
          <i />
          <span className="c99h-dh-pill">
            <span className="cr">
              <CrownSimpleIcon weight="fill" />
            </span>
            99 Percentile Challenge
          </span>
        </div>
        <span className="c99h-dh-cnt">
          Week <b>{weekNo}</b>
          <i />
          Day <b>{dayNo}</b> of 99
        </span>
      </div>
      {/* Matches the prototype's dashHeader() exactly: Math.max(0, 99 - dayNo) - i.e. days left in
          the challenge itself, not a real JEE Main exam date (the prototype has no such date in its
          data model either - the "to JEE Main" label is its own simplification, kept as-is here). */}
      <div className="c99h-dh-c">
        <div>
          <b className="c99h-num">{Math.max(0, 99 - dayNo)} days</b>
          <span>to JEE Main</span>
        </div>
      </div>
    </header>
  );
}

function TaskAction({ task, onStart, onViewAnalysis, onGoToListing, preferredPackId }) {
  const xp = task.xpOnTime;

  if (task.state === 'done') {
    const packId = preferredPackId || task.testId?.packDetails?.[0]?.packId;
    return (
      <button
        className="c99h-tk-go"
        disabled={!packId}
        onClick={() => packId && onViewAnalysis(packId, task.testId._id)}
        type="button"
      >
        <span className="c99h-tk-gl">
          <b>
            View analysis
            <ArrowRightIcon weight="bold" />
          </b>
        </span>
      </button>
    );
  }

  if (task.state === 'locked') {
    return (
      <span className="c99h-tk-wt">
        <span className="c99h-tk-gl">
          <b>
            <LockSimpleIcon />
            Locked
          </b>
          <small>
            <LightningIcon weight="fill" />
            {xp} XP
          </small>
        </span>
      </span>
    );
  }

  // Today's own calendar day has arrived but the matched test's own start time (task.launchesAt)
  // hasn't - distinct from 'locked' (which is about the challenge day itself, not this test's clock
  // time). Not counted toward the day's "X of Y mocks" total either (see Planner) until it flips to
  // 'open', so the denominator grows as each test actually goes live rather than all at once at
  // midnight.
  if (task.state === 'upcoming') {
    return (
      <span className="c99h-tk-wt">
        <span className="c99h-tk-gl">
          <b>
            <ClockIcon />
            {task.launchesAt
              ? `Starts at ${new Date(task.launchesAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
              : 'Starts later today'}
          </b>
          <small>
            <LightningIcon weight="fill" />
            {xp} XP
          </small>
        </span>
      </span>
    );
  }

  // A future day's special-mock task resolving to 'early' is technically attemptable server-side
  // (XP capped at that day's own multiplier, not boosted) - but the product call is not to surface
  // that early-access path in the UI, so it gets the same disabled "Upcoming" treatment as
  // 'upcoming' (today's task whose own clock time hasn't arrived) instead of an enabled button.
  if (task.state === 'early') {
    return (
      <span className="c99h-tk-wt">
        <span className="c99h-tk-gl">
          <b>
            <ClockIcon />
            Upcoming
          </b>
          <small>
            <LightningIcon weight="fill" />
            {xp} XP
          </small>
        </span>
      </span>
    );
  }

  // open / missed are both attemptable - missed gets a "Catch up" label instead of "Start test".
  const label = task.state === 'missed' ? 'Catch up' : 'Start test';
  const alt = task.state === 'missed';

  // A pool-matched task (testTypeMatch set - resolveDayTask in challengeHomeService.js) has no
  // single fixed test of its own - task.testId here is only an arbitrary representative pick used
  // to show duration/questions/marks stats, not a commitment to that specific test. Same as QPT,
  // this goes to the listing screen so the student picks for themselves, instead of deep-linking
  // into whichever test happened to be the representative one.
  if (task.testTypeMatch) {
    return (
      <button className={`c99h-tk-go${alt ? ' alt' : ' shimmer'}`} onClick={onGoToListing} type="button">
        <span className="c99h-tk-gl">
          <b>
            {label}
            <ArrowRightIcon weight="bold" />
          </b>
          <small>
            <LightningIcon weight="fill" />
            {xp} XP
          </small>
        </span>
      </button>
    );
  }

  // Prefer the user's own purchased JEE Main pack (preferredPackId - resolved in Challenge99Home
  // from purchasedPacks by exam id) over the test's own catalog packDetails - a test can be
  // attached to a different/arbitrary pack in the catalog (or none at all, e.g. the subject-wise
  // "Special PYQ Mock" tests, packDetails: []), but the student should always land in their own
  // actual purchased pack rather than whatever pack the catalog happens to associate the test with.
  const packId = preferredPackId || task.testId?.packDetails?.[0]?.packId;
  const disabled = !packId;

  return (
    <button
      className={`c99h-tk-go${alt ? ' alt' : ' shimmer'}`}
      disabled={disabled}
      onClick={() => !disabled && onStart(packId, task.testId._id)}
      type="button"
    >
      <span className="c99h-tk-gl">
        <b>
          {label}
          <ArrowRightIcon weight="bold" />
        </b>
        <small>
          <LightningIcon weight="fill" />
          {xp} XP
        </small>
      </span>
    </button>
  );
}

function TaskCard({ task, onStart, onViewAnalysis, onGoToListing, preferredPackId }) {
  const { cls, Icon, iconUrl, kind } = subjectMeta(task);
  const title = task.testId?.title || task.testId?.shortTitle || kind;

  let chip = null;
  if (task.state === 'done') {
    chip = (
      <span className="c99h-tk-chip ok">
        <CheckCircleIcon weight="fill" />
        Done
      </span>
    );
  } else if (task.state === 'missed') {
    chip = (
      <span className="c99h-tk-chip warn">
        <WarningCircleIcon weight="fill" />
        Missed
      </span>
    );
  }
  // 'early'/'upcoming' get no chip here - the action pill already says "Upcoming"/"Starts at ...",
  // so a second "Upcoming" label next to the kind would just be redundant.

  // Straight off the Test doc (getHome()'s testId populate) - nullable if the underlying Test
  // doc doesn't have them set, so only render each when present rather than showing a zero.
  const duration = task.testId?.totalTime;
  const questionCount = task.testId?.totalQuestions;
  const totalMarks = task.testId?.totalMarks;

  // Same pool-choice signal as the syllabus check below (task.testTypeMatch only appears on a
  // pool-resolved row) - but only while the choice is still live: once 'done' the pool resolved to
  // one specific attempted test (the card already reads "View analysis" for that one), and 'locked'
  // has nothing to choose from yet, so the hint would be misleading in either of those.
  const showPoolHint = Boolean(task.testTypeMatch) && task.state !== 'done' && task.state !== 'locked';

  return (
    <li className={`c99h-tk ${cls} is-${task.state}`}>
      <span className="c99h-tk-art">{iconUrl ? <img src={iconUrl} alt="" /> : <Icon weight="fill" />}</span>
      <div className="c99h-tk-tx">
        <div className="c99h-tk-hd">
          <span className="c99h-tk-k">{kind}</span>
          {chip}
        </div>
        <h3>{title}</h3>
        {showPoolHint && (
          <p className="c99h-tk-note">
            Any test from this pool counts - attempt whichever one you like to earn the XP.
          </p>
        )}
        {(duration != null || questionCount != null || totalMarks != null || task.state !== 'done') && (
          <div className="c99h-tk-m">
            {duration != null && (
              <span>
                <TimerIcon />
                {duration} min
              </span>
            )}
            {questionCount != null && (
              <span>
                <ListChecksIcon />
                {questionCount} Qs
              </span>
            )}
            {totalMarks != null && (
              <span>
                <GaugeIcon />
                {totalMarks} marks
              </span>
            )}
            {/* Hidden by default (.xp-m), shown only on a narrow card via the @container query -
                the button's own XP line hides at that width, so this is a relocation, not a
                duplicate (same rule the prototype uses for its mobile task cards). */}
            {task.state !== 'done' && (
              <span className="xp xp-m">
                <LightningIcon weight="fill" />
                {task.xpOnTime} XP
              </span>
            )}
          </div>
        )}
      </div>
      <div className="c99h-tk-ft">
        <TaskAction
          task={task}
          onStart={onStart}
          onViewAnalysis={onViewAnalysis}
          onGoToListing={onGoToListing}
          preferredPackId={preferredPackId}
        />
      </div>
      {/* Only a ChallengeTask row that was assigned one specific, fixed test has a single syllabus
          worth showing - task.subject isn't that signal (e.g. a fixed HFT/topic test can be a
          specific test with real syllabus content but no subject reference of its own). The actual
          distinction the backend itself uses in resolveDayTask is task.testId being set on the raw
          row (fixed) vs. resolved from a pool via testTypeMatch - testTypeMatch only ever appears on
          the latter (see challengeHomeService.js's day-task query), so its absence here means this
          card's testId was a specific, pinned test rather than "whichever test from this category."
          Spans the full card width as its own row (see .c99h-tk-syl's grid-column) rather than
          living inside the narrower text column. */}
      {!task.testTypeMatch && task.testId?.syllabus && (
        <div className="c99h-tk-syl" dangerouslySetInnerHTML={{ __html: task.testId.syllabus }} />
      )}
    </li>
  );
}

function QptCard({ qpt, onGoToListing, onViewAnalysis }) {
  if (!qpt) return null;

  let chip = null;
  let action;
  // Only the attemptable (open) state actually offers the pool choice - 'done' already resolved to
  // one specific test, 'upcoming' has nothing live to pick from yet, so the hint would be misleading
  // in either of those.
  let poolHint = null;
  if (qpt.state === 'done') {
    chip = (
      <span className="c99h-tk-chip ok">
        <CheckCircleIcon weight="fill" />
        Done
      </span>
    );
    // testId/packId identify which specific test (out of the pool) satisfied this week's credit -
    // only present once the backend resolves the counted ChallengeAttempt row, so fall back to the
    // old static label if either is missing rather than linking to a broken analysis page.
    action =
      qpt.testId && qpt.packId ? (
        <button className="c99h-tk-go" onClick={() => onViewAnalysis(qpt.packId, qpt.testId)} type="button">
          <span className="c99h-tk-gl">
            <b>
              View analysis
              <ArrowRightIcon weight="bold" />
            </b>
          </span>
        </button>
      ) : (
        <span className="c99h-tk-wt">
          <span className="c99h-tk-gl">
            <b>
              <CheckCircleIcon weight="fill" />
              Completed this week
            </b>
          </span>
        </span>
      );
  } else if (qpt.state === 'upcoming') {
    // Same launch-gate concept as a day task's 'upcoming' state - the pool has tests, but none of
    // them have hit their own startDate yet, so there's nothing live to actually start. xpOnTime is
    // null for this state (resolveQptState), matching the day-task 'upcoming'/'early' pill's lack
    // of an XP line.
    action = (
      <span className="c99h-tk-wt">
        <span className="c99h-tk-gl">
          <b>
            <ClockIcon />
            {qpt.launchesAt
              ? `Starts at ${new Date(qpt.launchesAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
              : 'Starts soon'}
          </b>
        </span>
      </span>
    );
  } else {
    // QPT is a pool, not one fixed test - the API doesn't resolve a single testId for it (and
    // can't, since the student picks which part test to attempt) - so this goes to the test
    // listing screen instead of a details page, unlike the fixed day-scoped tasks above.
    poolHint = 'Any part test from this pool counts - attempt whichever one you like to earn the XP.';
    action = (
      <button className="c99h-tk-go shimmer" onClick={onGoToListing} type="button">
        <span className="c99h-tk-gl">
          <b>
            Start test
            <ArrowRightIcon weight="bold" />
          </b>
          {qpt.xpOnTime != null && (
            <small>
              <LightningIcon weight="fill" />
              {qpt.xpOnTime} XP
            </small>
          )}
        </span>
      </button>
    );
  }

  return (
    <li className={`c99h-tk qpt is-${qpt.state}`}>
      <span className="c99h-tk-art">
        <ExamIcon weight="fill" />
      </span>
      <div className="c99h-tk-tx">
        <div className="c99h-tk-hd">
          <span className="c99h-tk-k">Part Test</span>
          {chip}
        </div>
        {/* title mirrors label (resolveQptState) - same uniform field day-task cards read via
            testId.title, so both card types can share one heading convention. */}
        <h3>{qpt.title || qpt.label || 'Quizrr Part Test'}</h3>
        {poolHint && <p className="c99h-tk-note">{poolHint}</p>}
        {(qpt.totalTime != null || qpt.totalQuestions != null || qpt.totalMarks != null || qpt.xpOnTime != null) && (
          <div className="c99h-tk-m">
            {qpt.totalTime != null && (
              <span>
                <TimerIcon />
                {qpt.totalTime} min
              </span>
            )}
            {qpt.totalQuestions != null && (
              <span>
                <ListChecksIcon />
                {qpt.totalQuestions} Qs
              </span>
            )}
            {qpt.totalMarks != null && (
              <span>
                <GaugeIcon />
                {qpt.totalMarks} marks
              </span>
            )}
            {qpt.xpOnTime != null && (
              <span className="xp xp-m">
                <LightningIcon weight="fill" />
                {qpt.xpOnTime} XP
              </span>
            )}
          </div>
        )}
      </div>
      <div className="c99h-tk-ft">{action}</div>
    </li>
  );
}

function WeekPicker({ viewedDay, realDayNo, activityByDay, onSelectDay }) {
  const mon = viewedDay - mondayOffsetOf(resolveDate(viewedDay, activityByDay));
  const canPrev = mon > 1;
  const canNext = mon + 7 <= 99;

  const days = Array.from({ length: 7 }, (_, i) => mon + i);

  return (
    <div className="c99h-wn">
      <button
        className="c99h-ar"
        disabled={!canPrev}
        onClick={() => onSelectDay(Math.max(1, mon - 7))}
        aria-label="Previous week"
        type="button"
      >
        <CaretLeftIcon weight="bold" />
      </button>
      <div className="c99h-wk-row" role="tablist" aria-label={`Week ${Math.ceil(mon / 7) || 1}`}>
        {days.map((d) => {
          const status = activityByDay.get(d)?.status;
          const disabled = d < 1 || d > 99 || status === 'before_join';
          const dt = resolveDate(d, activityByDay);
          return (
            <button
              key={d}
              className={`c99h-dy${d === realDayNo ? ' now' : ''}${d === viewedDay ? ' sel' : ''}${d > realDayNo ? ' fut' : ''}`}
              role="tab"
              aria-selected={d === viewedDay}
              disabled={disabled}
              onClick={() => onSelectDay(d)}
              type="button"
            >
              <span>{dowLabel(dt)}</span>
              <i>
                <em>{dt.getDate()}</em>
              </i>
            </button>
          );
        })}
      </div>
      <button
        className="c99h-ar"
        disabled={!canNext}
        onClick={() => onSelectDay(Math.min(99, mon + 7))}
        aria-label="Next week"
        type="button"
      >
        <CaretRightIcon weight="bold" />
      </button>
    </div>
  );
}

function SocialProofStrip({ socialProof, isToday }) {
  if (!socialProof || !socialProof.count) return null;
  const avatars = (socialProof.avatars || []).slice(0, 4);
  // `phase` only applies to today's own window (the first hour after today's task actually goes
  // live, before a full-length attempt could realistically be finished) - a past day is always
  // fully elapsed, so its copy never varies by phase.
  const verb = isToday && socialProof.phase === 'started' ? 'have started' : 'completed';

  return (
    <div className="c99h-pf">
      {avatars.length > 0 && (
        <span className="c99h-pf-av">
          {avatars.map((url, i) => (
            <img key={i} src={url} alt="" loading="lazy" />
          ))}
        </span>
      )}
      <span className="c99h-pf-t">
        <b>{socialProof.count.toLocaleString('en-IN')}</b> {verb} {isToday ? "today's task" : 'these HFTs'}
      </span>
    </div>
  );
}

function Planner({
  viewedDay,
  realDayNo,
  tasks,
  qpt,
  socialProof,
  activityByDay,
  onSelectDay,
  onStart,
  onViewAnalysis,
  onGoToListing,
  preferredPackId,
  hasStarted,
  joinedCount,
}) {
  const isToday = viewedDay === realDayNo;
  // The QPT slot is only ever rendered on the "today" view (see below), so it shouldn't count
  // toward a different day's total - otherwise a day with zero real tasks never hits the empty state.
  // Exhausted (no tests of that type left across the whole challenge) isn't a task to do or have
  // done either - there's nothing left for the student to act on, so it's excluded entirely rather
  // than shown as a dead card or silently counted into the denominator.
  const showQpt = isToday && qpt && qpt.state !== 'exhausted';
  // 'upcoming' (today's day arrived, but the matched test's own start time hasn't - see TaskAction)
  // still counts toward the day's total - it's a real mock scheduled for today, just not live yet,
  // so the denominator should reflect the actual day plan rather than read "0 of 0" until release.
  const doneCount = tasks.filter((t) => t.state === 'done').length + (showQpt && qpt.state === 'done' ? 1 : 0);
  const total = tasks.length + (showQpt ? 1 : 0);
  const hasAnyTasks = total > 0;
  const dt = resolveDate(viewedDay, activityByDay);

  return (
    <section className="c99h-pl" aria-labelledby="c99hPlT">
      <WeekPicker viewedDay={viewedDay} realDayNo={realDayNo} activityByDay={activityByDay} onSelectDay={onSelectDay} />
      {joinedCount != null && (
        <div className="c99h-fomo">
          <UsersThreeIcon weight="fill" className="c99h-fomo-ic" />
          <span>
            <b>{joinedCount.toLocaleString('en-IN')}</b> students are grinding this challenge with you - keep up!
          </span>
        </div>
      )}
      {!hasStarted && <p className="c99h-pl-pre">Your first HFT drops tomorrow at 3 PM, all the best!</p>}
      <div className="c99h-pl-h">
        <div>
          <h2 id="c99hPlT">
            {isToday
              ? hasStarted
                ? "Today's HFT"
                : 'Upcoming HFT'
              : `${dowLabel(dt)}, ${dt.getDate()} ${MON[dt.getMonth()]}`}
          </h2>
          <p>
            <span className="c99h-pl-sub">
              Day {viewedDay} &middot; <b>{doneCount}</b> of {total} HFTs done
            </span>
            {!isToday && (
              <button className="c99h-bk" onClick={() => onSelectDay(realDayNo)} type="button">
                Back to today
              </button>
            )}
          </p>
        </div>
        {viewedDay <= realDayNo && <SocialProofStrip socialProof={socialProof} isToday={isToday} />}
      </div>
      {!hasAnyTasks ? (
        <div className="c99h-empty">
          <span className="c99h-empty-art">
            <CalendarDotsIcon weight="duotone" />
          </span>
          {viewedDay > realDayNo ? (
            <>
              <h3>HFT for the Day {viewedDay} isn&rsquo;t out yet</h3>
              <p>
                It&rsquo;ll be released closer to the day. Finish this week&rsquo;s HFTs to stay on track in the
                meantime.
              </p>
            </>
          ) : (
            <h3>No HFTs scheduled for this day.</h3>
          )}
          {!isToday && (
            <button className="c99h-tk-go alt" onClick={() => onSelectDay(realDayNo)} type="button">
              <span className="c99h-tk-gl">
                <b>
                  Go to today&rsquo;s HFT
                  <ArrowRightIcon weight="bold" />
                </b>
              </span>
            </button>
          )}
        </div>
      ) : (
        <ul className="c99h-tk-ls">
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              onStart={onStart}
              onViewAnalysis={onViewAnalysis}
              onGoToListing={onGoToListing}
              preferredPackId={preferredPackId}
            />
          ))}
          {showQpt && <QptCard qpt={qpt} onGoToListing={onGoToListing} onViewAnalysis={onViewAnalysis} />}
        </ul>
      )}
    </section>
  );
}

const STREAK_DOT_STATUS = {
  before_join: 'pre',
  future: 'fut',
  pending: 'pend',
  missed: 'miss',
  full: 'on',
  partial: 'on',
};

function StreakCard({
  currentStreak,
  bestStreak,
  realDayNo,
  activityByDay,
  myRank,
  totalParticipants,
  isHftUpcoming,
  isTodayActive,
}) {
  const active = currentStreak > 0;
  const mon = realDayNo - mondayOffsetOf(resolveDate(realDayNo, activityByDay));
  const weekDays = Array.from({ length: 7 }, (_, i) => mon + i);

  return (
    <section className={`c99h-sd c99h-sk${active ? '' : ' off'}`} aria-label="Day streak">
      <div className="c99h-sk-h">
        <div>
          <h3>
            <span className="c99h-num">{currentStreak}</span> day streak
            {/* Mobile only (hidden on desktop via CSS) - the rank tile itself is hidden on mobile
                now, so its number moves up here instead of disappearing from the fold entirely. */}
            <span className="c99h-sk-rank">
              <i>|</i>
              Rank: {myRank ? `#${myRank.toLocaleString('en-IN')}` : '—'}
              {totalParticipants != null && <small>of {totalParticipants.toLocaleString('en-IN')}</small>}
            </span>
          </h3>
          <p>
            {/* Today's HFT may not have gone live yet (task.state 'upcoming', its own start time
                hasn't arrived even though the calendar day has) - saying "finish the HFT" then
                would read as attemptable right now when it isn't. */}
            {active
              ? isHftUpcoming
                ? 'Finish the upcoming HFT before midnight to keep it going.'
                : 'Finish the HFT before midnight to keep it going.'
              : isHftUpcoming
                ? 'Finish the upcoming HFT today to start a new streak!'
                : 'Finish the HFT today to start a new streak!'}
          </p>
          {/* Always shown, regardless of active/off state - a day with nothing scheduled
              (recomputeStreak's hasAssignableTasks check) never counts as a miss, so this reassures
              the same way whether the card is currently showing active or broken. Not gated on
              `active` - that's exactly the case that kept looking "not shown" when the account's
              own streak was never actually 0. */}
          <p className="c99h-sk-note">
            Days when you had nothing to attempt (like some Sundays) are skipped automatically and never break your
            streak.
          </p>
          {bestStreak > 0 && <p>Best streak: {bestStreak} days</p>}
        </div>
        <FireIcon weight="duotone" className="c99h-sk-f" />
      </div>
      <div className="c99h-wk">
        {weekDays.map((d) => {
          const status = activityByDay.get(d)?.status;
          // activity-grid reports today as 'pending' unconditionally until the day rolls over
          // (server-side, by design - today's tasksDone/tasksTotal "isn't final until the day
          // ends"), so today's own dot has to be derived from the already-fetched live task
          // state instead of trusted from the grid, same as every other day's dot is.
          const dotState =
            d < 1 || d > 99 ? 'pre' : d === realDayNo && isTodayActive ? 'on' : STREAK_DOT_STATUS[status] || 'fut';
          return (
            <div key={d} className={`${dotState}${d === realDayNo ? ' now' : ''}`}>
              {dowLabel(resolveDate(d, activityByDay))[0]}
              <i>{dotState === 'on' && <CheckIcon weight="bold" />}</i>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// No "partly done" distinction on the grid - any activity that day (partial or full) reads as
// done, matching the grid's actual purpose (did you show up that day), not task-by-task completion.
const GRID_DOT_STATUS = {
  before_join: 'pre',
  future: 'fut',
  pending: 'pend',
  missed: 'miss',
  full: 'full',
  partial: 'full',
};

function ActivityGrid99({ days, realDayNo, currentStreak, bestStreak, onSelectDay, isTodayActive }) {
  if (!days.length) return null;

  const byDay = new Map(days.map((d) => [d.dayNo, d]));
  // Days run left to right, wrapping into rows of this many columns.
  const columnCount = 15;

  let full = 0;
  let missed = 0;
  const cells = [];
  for (let k = 1; k <= 99; k++) {
    const entry = byDay.get(k);
    const status = entry?.status;
    // Same override as StreakCard's dots - today's grid status is always 'pending' server-side
    // by design, so derive it from the live task state already in hand instead.
    const cls = k === realDayNo && isTodayActive ? 'full' : GRID_DOT_STATUS[status] || 'fut';
    if (cls === 'full') full++;
    else if (cls === 'miss') missed++;
    const dt = resolveDate(k, byDay);
    cells.push(
      <button
        key={k}
        className={`c99h-d99 ${cls}${k === realDayNo ? ' now' : ''}`}
        title={`Day ${k} · ${dowLabel(dt)}, ${dt.getDate()} ${MON[dt.getMonth()]}${
          entry && status !== 'future' && status !== 'before_join'
            ? ` · ${entry.tasksDone} of ${entry.tasksTotal} done`
            : ''
        }`}
        aria-label={`Day ${k}`}
        onClick={() => onSelectDay(k)}
        type="button"
      >
        <i>{cls === 'full' ? <CheckIcon weight="bold" /> : <span className="c99h-d99-n">{k}</span>}</i>
      </button>,
    );
  }

  return (
    <section className="c99h-sd c99h-g99" id="c99h-g99-section" aria-labelledby="c99hG99T">
      <div className="c99h-g99-h">
        <div>
          <h3 id="c99hG99T">Your 99 days</h3>
          <p>
            {DOW_DATE(resolveDate(1, byDay))} &ndash; {DOW_DATE(resolveDate(99, byDay))}
          </p>
        </div>
        <div className="c99h-g99-st">
          <div className="cur">
            <span className="c99h-g99-si">
              <FireIcon weight="fill" />
            </span>
            <div>
              <b>
                <i className="c99h-num">{currentStreak}</i> {currentStreak === 1 ? 'day' : 'days'}
              </b>
              <span>Current streak</span>
            </div>
          </div>
          <div className="best">
            <span className="c99h-g99-si">
              <MedalIcon weight="fill" />
            </span>
            <div>
              <b>
                <i className="c99h-num">{bestStreak}</i> {bestStreak === 1 ? 'day' : 'days'}
              </b>
              <span>Best streak</span>
            </div>
          </div>
        </div>
      </div>
      <div className="c99h-g99-g" style={{ '--w': columnCount }}>
        {cells}
      </div>
      <div className="c99h-g99-lg">
        <span>
          <i className="full" />
          <b className="c99h-num">{full}</b> active
        </span>
        <span>
          <i className="miss" />
          <b className="c99h-num">{missed}</b> missed
        </span>
      </div>
    </section>
  );
}

const DOW_DATE = (dt) => `${dt.getDate()} ${MON[dt.getMonth()]} ${dt.getFullYear()}`;

// Display metadata (name/icon/gradient/category/how-to-earn copy) for each of the 15 badges -
// keyed by the badgeId the backend returns (constants/challenge.js CHALLENGE_BADGE_CATALOG).
// `category` and `desc` mirror that file's category field and inline comments (which themselves
// mirror the prototype's BDG array) - this is still presentation-only, earn/lock state and counts
// always come from GET /api/challenge/badges.
const BADGE_META = {
  FIRST_STEP: {
    name: 'First Step',
    Icon: FootprintsIcon,
    g1: '#a78bfa',
    g2: '#6d28d9',
    category: 'Consistency',
    desc: 'Finish your first HFT of the challenge.',
  },
  PART_TEST_PRO: {
    name: 'Part Test Pro',
    Icon: ExamIcon,
    g1: '#ff8a9e',
    g2: '#e11d48',
    category: 'Tests',
    desc: 'Finish a Quizrr Part Test.',
  },
  HAT_TRICK: {
    name: 'Hat-trick',
    Icon: FireIcon,
    g1: '#ffc061',
    g2: '#ff4d1f',
    category: 'Consistency',
    desc: 'Keep a 3-day streak. Won again for every 3 days in a row.',
  },
  ALL_ROUNDER: {
    name: 'All-Rounder',
    Icon: BooksIcon,
    g1: '#8f9bff',
    g2: '#4338ca',
    category: 'Tests',
    desc: 'Finish a HFT in each of Physics, Chemistry and Mathematics. Won again for every full set.',
  },
  PERFECT_DAY: {
    name: 'Perfect Day',
    Icon: CheckCircleIcon,
    g1: '#5ee88f',
    g2: '#14a046',
    category: 'Consistency',
    desc: 'Finish every HFT of the day.',
  },
  COMEBACK: {
    name: 'Comeback',
    Icon: ArrowsClockwiseIcon,
    g1: '#6cb6ff',
    g2: '#2563eb',
    category: 'Consistency',
    desc: 'Catch up on a HFT you missed.',
  },
  FULL_TEST_FINISHER: {
    name: 'Full Test Finisher',
    Icon: ClipboardTextIcon,
    g1: '#d19bff',
    g2: '#7c3aed',
    category: 'Tests',
    desc: 'Finish a Quizrr Full Test.',
  },
  WEEK_WARRIOR: {
    name: 'Week Warrior',
    Icon: CalendarCheckIcon,
    g1: '#4ee3b0',
    g2: '#0c9d94',
    category: 'Consistency',
    desc: 'Keep a 7-day streak. Won again for every 7 days in a row.',
  },
  SHARPSHOOTER: {
    name: 'Sharpshooter',
    Icon: TargetIcon,
    g1: '#ff9a8a',
    g2: '#e5383b',
    category: 'Tests',
    desc: 'Score 90% or more in any HFT or test.',
  },
  PERFECT_WEEK: {
    name: 'Perfect Week',
    Icon: CalendarStarIcon,
    g1: '#ff8cc6',
    g2: '#e0287a',
    category: 'Consistency',
    desc: 'Finish every HFT, every day, for a full week.',
  },
  TOP_10_PERCENT: {
    name: 'Top 10%',
    Icon: RankingIcon,
    g1: '#ffd84d',
    g2: '#f08c00',
    category: 'Rank',
    desc: 'End a week in the top 10% of the Weekly All India Rank.',
  },
  UNSTOPPABLE: {
    name: 'Unstoppable',
    Icon: LightningIcon,
    g1: '#ffe55c',
    g2: '#f59f00',
    category: 'Consistency',
    desc: 'Keep a 21-day streak. Won again for every 21 days in a row.',
  },
  PODIUM: {
    name: 'Podium',
    Icon: CrownIcon,
    g1: '#ffe27a',
    g2: '#ff9f1a',
    category: 'Rank',
    desc: 'End a week in the top 3 of the Weekly All India Rank.',
  },
  NINETY_NINE_CLUB: {
    name: '99 Club',
    Icon: DiamondIcon,
    g1: '#b8a6ff',
    g2: '#5b21b6',
    category: 'Rank',
    desc: 'Be active on all 99 days of the challenge.',
  },
};
const BADGE_ORDER = Object.keys(BADGE_META);

function BadgeDetailSheet({ badgeId, badge, onClose }) {
  const meta = BADGE_META[badgeId];
  const Icon = meta.Icon;
  const earned = Boolean(badge.earned);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="c99h-mx">
      <div className="c99h-mx-bg" onClick={onClose} />
      <section
        className="c99h-bdl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="c99hBdlT"
        style={{ '--g1': meta.g1, '--g2': meta.g2 }}
      >
        <ModalCloseButton onClick={onClose} />
        <div className="c99h-bdl-body">
          <span className={`c99h-bd-m big${earned ? '' : ' off'}`}>
            <Icon weight="fill" />
          </span>
          <span className="c99h-bdl-k">{meta.category}</span>
          <h3 id="c99hBdlT">{meta.name}</h3>
          <div className="c99h-bdl-w">
            <b className="c99h-num">{badge.timesWon || 0}</b>
            <span>{badge.timesWon === 1 ? 'time won' : 'times won'}</span>
          </div>
          <div className="c99h-bdl-how">
            <span>How to earn</span>
            <p>{meta.desc}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function BadgesSection({ badges }) {
  const [openBadgeId, setOpenBadgeId] = useState(null);
  if (!badges.length) return null;
  const won = badges.filter((b) => b.earned).length;
  const byId = new Map(badges.map((b) => [b.badgeId, b]));
  const openBadge = openBadgeId ? byId.get(openBadgeId) : null;

  return (
    <section className="c99h-sd c99h-bdc" aria-labelledby="c99hBdT">
      <div className="c99h-g99-h">
        <div>
          <h3 id="c99hBdT">Badges</h3>
          <p>
            <b className="c99h-num">{won}</b> of 14 unlocked. Tap a badge to see how to earn it.
          </p>
        </div>
      </div>
      <div className="c99h-bd-g">
        {BADGE_ORDER.map((badgeId) => {
          const meta = BADGE_META[badgeId];
          const badge = byId.get(badgeId);
          if (!badge) return null;
          const Icon = meta.Icon;
          return (
            <button
              key={badgeId}
              className={`c99h-bd${badge.earned ? '' : ' off'}`}
              style={{ '--g1': meta.g1, '--g2': meta.g2 }}
              title={meta.name}
              onClick={() => setOpenBadgeId(badgeId)}
              type="button"
            >
              <span className="c99h-bd-m">
                <Icon weight="fill" />
                {badge.earned && badge.timesWon > 1 && <em>&times;{badge.timesWon}</em>}
              </span>
              <b>{meta.name}</b>
              {!badge.earned && <small>Locked</small>}
            </button>
          );
        })}
      </div>
      {openBadge && <BadgeDetailSheet badgeId={openBadgeId} badge={openBadge} onClose={() => setOpenBadgeId(null)} />}
    </section>
  );
}

// Desktop only - hidden entirely on mobile (.c99h-sd-s{display:none} in the @media(1020px) block),
// where its rank number instead reads inline on the streak card's heading (.c99h-sk-rank).
function RankCard({ myRank, totalParticipants }) {
  return (
    <div className="c99h-sd-s">
      <TrophyIcon weight="fill" />
      <b className="c99h-num">
        {myRank ? `#${myRank.toLocaleString('en-IN')}` : '—'}
        {totalParticipants != null && <small>of {totalParticipants.toLocaleString('en-IN')}</small>}
      </b>
      <span>Weekly All India Rank</span>
    </div>
  );
}

const initials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
};

// Falls back to initials both when there's no profilePicture at all AND when the URL is present but
// fails to actually load (expired/hotlink-blocked Google avatar URLs do this - a truthy src isn't
// enough on its own to know the image will render).
function Avatar({ src, name, fallbackClassName }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <span className={fallbackClassName}>{initials(name)}</span>;
  }
  return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

// Matches the prototype's .dr transition duration (0.28s) - the close handler waits this long
// before actually unmounting, so the slide-out has time to play instead of the drawer just vanishing.
const DRAWER_ANIM_MS = 280;

function LeaderboardDrawer({ leaderboard, myRank, weekNo, totalParticipants, me, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    // Mount closed (translated off-screen, backdrop transparent), then flip to open on the next
    // frame so the transition actually has a "from" state to animate out of - setting the class in
    // the same render as the initial mount would skip straight to the end state instead of sliding.
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => {
      document.body.style.overflow = '';
      cancelAnimationFrame(raf);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, DRAWER_ANIM_MS);
  };

  return (
    <div className={`c99h-mx c99h-dr-mx${visible ? ' on' : ''}`}>
      <div className="c99h-mx-bg" onClick={handleClose} />
      <aside className="c99h-dr" role="dialog" aria-modal="true" aria-labelledby="c99hDrT">
        <ModalCloseButton onClick={handleClose} className="c99h-dr-close" />
        <header className="c99h-dr-h">
          <div>
            <h2 id="c99hDrT">Weekly Leaderboard</h2>
            <p>
              Week {weekNo}
              {totalParticipants != null && <> &middot; {totalParticipants.toLocaleString('en-IN')} students</>}
            </p>
          </div>
        </header>
        <ol className="c99h-dr-ls">
          {leaderboard.map((row, i) => {
            const rank = i + 1;
            const isMe = String(row.userId) === String(me?._id);
            return (
              <li key={row.userId} className={rank <= 3 ? `t${rank}` : undefined}>
                <span className="c99h-dr-r">{rank <= 3 ? <i className={`c99h-lb3-m m${rank}`}>{rank}</i> : rank}</span>
                <Avatar src={row.profilePicture} name={row.name} fallbackClassName="c99h-dr-av" />
                <span className="c99h-dr-n">{isMe ? 'You' : row.name}</span>
                <span className="c99h-dr-x2 c99h-num">
                  <LightningIcon weight="fill" />
                  {row.xp.toLocaleString('en-IN')}
                </span>
              </li>
            );
          })}
        </ol>
        {myRank && (
          <footer className="c99h-dr-me">
            <span className="c99h-dr-r c99h-num">{myRank.rank}</span>
            <Avatar src={me?.profilePicture} name={me?.name} fallbackClassName="c99h-dr-av" />
            <div>
              <b>You</b>
              {totalParticipants != null && <span>Out of {totalParticipants.toLocaleString('en-IN')} students</span>}
            </div>
            <span className="c99h-dr-x2 c99h-num">
              <LightningIcon weight="fill" />
              {myRank.xp.toLocaleString('en-IN')}
            </span>
          </footer>
        )}
      </aside>
    </div>
  );
}

function MiniLeaderboard({
  leaderboard,
  myRank,
  weekNo,
  currentWeekNo,
  loading,
  totalParticipants,
  me,
  onOpenFull,
  onSelectWeek,
}) {
  const top3 = leaderboard.slice(0, 3);
  const isEmpty = top3.length === 0 && !myRank;
  const canPrev = weekNo > 1;
  const canNext = weekNo < currentWeekNo;

  return (
    <section className="c99h-sd c99h-lbc" aria-labelledby="c99hLbT" aria-busy={loading}>
      <div className="c99h-sk-h">
        <div>
          <h3 id="c99hLbT">Weekly Leaderboard</h3>
          <p>
            Week {weekNo}
            {weekNo === currentWeekNo && <> &middot; This week</>}
          </p>
        </div>
        <div className="c99h-lb-nav">
          <button
            className="c99h-ar c99h-ar-sm"
            disabled={!canPrev || loading}
            onClick={() => onSelectWeek(weekNo - 1)}
            aria-label="Previous week"
            type="button"
          >
            <CaretLeftIcon weight="bold" />
          </button>
          <button
            className="c99h-ar c99h-ar-sm"
            disabled={!canNext || loading}
            onClick={() => onSelectWeek(weekNo + 1)}
            aria-label="Next week"
            type="button"
          >
            <CaretRightIcon weight="bold" />
          </button>
        </div>
      </div>
      {top3.length === 0 && !loading && <p className="c99h-lb-empty">No rankings for this week.</p>}
      {top3.length > 0 && (
        <ol className="c99h-lb3">
          {top3.map((row, i) => {
            const isMe = String(row.userId) === String(me?._id);
            return (
              <li key={row.userId}>
                <span className={`c99h-lb3-m m${i + 1}`}>{i + 1}</span>
                <Avatar src={row.profilePicture} name={row.name} fallbackClassName="c99h-lb3-av" />
                <span className="c99h-lb3-n">{isMe ? 'You' : row.name}</span>
                <span className="c99h-lb3-x c99h-num">
                  <LightningIcon weight="fill" />
                  {row.xp.toLocaleString('en-IN')}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {myRank && (
        <div className="c99h-lb3-me">
          <Avatar src={me?.profilePicture} name={me?.name} fallbackClassName="c99h-lb3-av" />
          <div>
            <b>You</b>
            <span>
              Rank <b className="c99h-num">{myRank.rank}</b>
              {totalParticipants != null && <> of {totalParticipants.toLocaleString('en-IN')}</>}
            </span>
          </div>
          <span className="c99h-lb3-x c99h-num">
            <LightningIcon weight="fill" />
            {myRank.xp.toLocaleString('en-IN')}
          </span>
        </div>
      )}
      <button className="c99h-lk2" onClick={onOpenFull} disabled={isEmpty} type="button">
        View full leaderboard
        <ArrowRightIcon weight="bold" />
      </button>
    </section>
  );
}

function RulesCard() {
  const rows = [
    {
      title: 'Attempt on the scheduled day',
      body: "Finish a day's HFT on the day it's scheduled for full XP. Catch up any time after - you'll still earn XP, just less.",
    },
    {
      title: 'Build your streak',
      body: "Attempt the assigned HFT every day or any test from the assigned pool to keep your streak alive. XP multipliers are highest early in each 7-day cycle and taper off toward the end. A day with no HFT scheduled (some Sundays, for example) won't break it either.",
    },
    {
      title: 'Submit before midnight',
      body: 'No matter when you start a test, you must submit it before 12 AM (midnight) on its scheduled day to maintain the streak.',
    },
    {
      title: 'More XP = Higher Rank',
      body: 'Your total XP decides your weekly rank on the All India leaderboard, which resets every Monday.',
    },
  ];

  return (
    <section className="c99h-sd c99h-rl" aria-labelledby="c99hRlT">
      <h3 id="c99hRlT">How it works</h3>
      <ol>
        {rows.map((row, i) => (
          <li key={row.title}>
            <span>{i + 1}</span>
            <div>
              <b>{row.title}</b>
              <p>{row.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function Challenge99Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { purchasedPacks, selectedPack } = usePackStorage();
  // First preference for every "Start test"/"View analysis" deep link: the user's own purchased
  // pack for the JEE Main exam specifically (this whole challenge is JEE-Main-scoped), not whatever
  // pack they currently have selected in the sidebar switcher (selectedPack can be a different test
  // series entirely) - selectedPack is only the last-resort fallback if, somehow, no purchased pack
  // matches the exam id (shouldn't happen given the page itself is gated on owning one).
  const jeeMainPackId = purchasedPacks?.find((pack) => pack.exam?._id === JEE_MAIN_EXAM_ID)?._id;
  const preferredPackId = jeeMainPackId || selectedPack?._id;
  const me = useSelector((state) => state.user?.user);
  const [showWelcome, setShowWelcome] = useState(Boolean(location.state?.justJoined));
  const [showFallingBehind, setShowFallingBehind] = useState(false);
  const [base, setBase] = useState(null); // GET /api/challenge/home (no day param) - "real today"
  const [viewedDay, setViewedDay] = useState(null);
  const [viewedHome, setViewedHome] = useState(null); // GET /api/challenge/home?day=N for the viewed day
  const [activityDays, setActivityDays] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ leaderboard: [], myRank: null, weekNo: null });
  const [badges, setBadges] = useState([]);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  // Past-week override for the widget + drawer only - `leaderboard` stays the current week so the
  // rank card keeps showing this week's rank while a student browses history.
  const [pastLeaderboard, setPastLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const leaderboardReq = useRef(0);
  const pastLeaderboardCache = useRef(new Map());
  const [loading, setLoading] = useState(true);
  // Total students who've ever joined the challenge (not today's completion count - that's
  // socialProof.count, a different number) - same /api/challenge/social-proof endpoint
  // Challenge99Landing polls for its own join ticker; the dashboard's FOMO strip does the same
  // (every 10s, see the effect below) so it visibly ticks up while a student is sitting on the page.
  const [joinedCount, setJoinedCount] = useState(null);
  const [promoBanner, setPromoBanner] = useState(null);

  useEffect(() => {
    if (!preferredPackId) return;
    // shortcut: shows only the top-priority banner, upgrade to a carousel if marketing queues several
    axios
      .get(`/api/banner/${preferredPackId}`, { params: { placement: 'challenge' } })
      .then((res) => {
        const list = []
          .concat(res?.data?.data || [])
          .filter((b) => b?.isVisible !== false && (b.banner || b.mobileBanner));
        list.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        setPromoBanner(list[0] || null);
      })
      .catch(() => setPromoBanner(null));
  }, [preferredPackId]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get('/api/challenge/home'),
      axios.get('/api/challenge/leaderboard').catch(() => null),
      axios.get('/api/challenge/activity-grid').catch(() => null),
      axios.get('/api/challenge/badges').catch(() => null),
      axios.get('/api/challenge/social-proof').catch(() => null),
    ])
      .then(([homeRes, lbRes, gridRes, badgesRes, spRes]) => {
        if (cancelled) return;
        const homeData = homeRes?.data?.data;
        setBase(homeData);
        setViewedDay(homeData.dayNo);
        setViewedHome(homeData);
        if (lbRes) setLeaderboard(lbRes.data.data);
        if (gridRes) setActivityDays(gridRes.data.data.days || []);
        if (badgesRes) setBadges(badgesRes.data.data.badges || []);
        if (spRes) setJoinedCount(spRes.data.data?.joinedCount ?? null);
        // Backend caps repeats itself (ChallengeMessageSeen), so showing it whenever present is
        // safe - no client-side dedup needed. Welcome takes priority on a first-join visit.
        if (!location.state?.justJoined && homeData.message?.key === 'falling_behind') {
          setShowFallingBehind(true);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) navigate('/challenge', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const currentWeekNo = leaderboard.currentWeekNo ?? base?.weekNo;
  const viewedLeaderboard = pastLeaderboard ?? leaderboard;
  const viewedWeekNo = viewedLeaderboard.weekNo ?? base?.weekNo;

  const handleSelectLeaderboardWeek = async (week) => {
    const reqId = ++leaderboardReq.current;
    if (week === currentWeekNo) {
      setPastLeaderboard(null);
      return;
    }
    // Past weeks are final, so they're cached for the session; the current week is never cached
    // here (it's the `leaderboard` state). The cache is dropped if the server's current week moves
    // on (session spanning a Monday rollover).
    const cached = pastLeaderboardCache.current.get(week);
    if (cached && cached.currentWeekNo === currentWeekNo) {
      setPastLeaderboard(cached);
      setLeaderboardLoading(false);
      return;
    }
    setLeaderboardLoading(true);
    try {
      const res = await axios.get('/api/challenge/leaderboard', { params: { week } });
      const data = res.data.data;
      if (data.currentWeekNo !== currentWeekNo) pastLeaderboardCache.current.clear();
      if (week < data.currentWeekNo) pastLeaderboardCache.current.set(week, data);
      if (reqId === leaderboardReq.current) setPastLeaderboard(data);
    } catch {
      // keep showing the previous week; arrows stay usable for a retry
    } finally {
      if (reqId === leaderboardReq.current) setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      axios
        .get('/api/challenge/social-proof')
        .then((res) => setJoinedCount(res.data.data?.joinedCount ?? null))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const activityByDay = useMemo(() => new Map(activityDays.map((d) => [d.dayNo, d])), [activityDays]);

  const handleSelectDay = (day) => {
    if (!base) return;
    setViewedDay(day);
    if (day === base.dayNo) {
      setViewedHome(base);
      return;
    }
    axios
      .get('/api/challenge/home', { params: { day } })
      .then((res) => setViewedHome(res.data.data))
      .catch(() => {});
  };

  const handleStart = (packId, testId) => {
    // UnlockedTestDetails' own back button otherwise always returns to the pack's generic test
    // listing - returnTo tells it to come back here instead. A query param (not navigate() state)
    // so it still works after a hard refresh of the details page, not just in-session. Kept as a
    // bare in-app path (not origin-qualified) since UnlockedTestDetails' back button feeds it
    // straight into react-router's navigate(), which expects a path, not a full URL - it's the one
    // that adds this app's own origin when forwarding the param on to the test-taking flow, which
    // runs on a different origin and needs the full URL to redirect back here after submission.
    navigate(`/${packId}/${testId}/details?returnTo=${encodeURIComponent('/challenge/home')}`);
  };

  const handleViewAnalysis = (packId, testId) => {
    navigate(`/${packId}/${testId}/analysis`);
  };

  // QPT has no single resolved test (it's a pool) - send the student to the listing screen for
  // their currently selected pack to pick one, instead of a details page.
  const handleGoToListing = () => {
    if (selectedPack?._id) navigate(`/v3/${selectedPack._id}/tests`);
  };

  const closeWelcome = () => {
    setShowWelcome(false);
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleViewMissedDays = () => {
    setShowFallingBehind(false);
    document.getElementById('c99h-g99-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // The AppLayout shell (sidenav included) renders immediately regardless of loading state, so
  // navigating here never blanks the whole screen - only the content slot shows a placeholder
  // while the initial fetches are in flight.
  const stillLoading = loading || !base || !viewedHome;

  // "Did the student show up today" - same any-activity-counts-as-done philosophy as
  // STREAK_DOT_STATUS/GRID_DOT_STATUS already use for every other day, computed from `base`
  // (always today's real task/qpt data, independent of whichever day is being viewed) since
  // activity-grid's own status for today is always 'pending' server-side until the day rolls
  // over - see the dotState/cls overrides in StreakCard/ActivityGrid99 below.
  const isTodayActive = !stillLoading && (base.tasks?.some((t) => t.state === 'done') || base.qpt?.state === 'done');

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack}>
      {stillLoading ? (
        <div className="c99h-loading" aria-hidden="true" />
      ) : (
        <>
          {showWelcome && <WelcomeMoment onClose={closeWelcome} />}
          {showFallingBehind && (
            <FallingBehindSheet
              missedCount={base.message?.missedCount ?? 1}
              onClose={() => setShowFallingBehind(false)}
              onViewMissed={handleViewMissedDays}
            />
          )}
          <div className="c99h">
            <div className="c99h-l">
              <ChallengeBanner dayNo={base.dayNo} weekNo={base.weekNo} joinedCount={joinedCount} />
              <PromoBanner banner={promoBanner} />
              <Planner
                viewedDay={viewedDay}
                realDayNo={base.dayNo}
                tasks={viewedHome.tasks}
                qpt={base.qpt}
                socialProof={viewedHome.socialProof}
                activityByDay={activityByDay}
                onSelectDay={handleSelectDay}
                onStart={handleStart}
                onViewAnalysis={handleViewAnalysis}
                preferredPackId={preferredPackId}
                onGoToListing={handleGoToListing}
                hasStarted={base.hasStarted !== false}
                joinedCount={joinedCount}
              />
              <ActivityGrid99
                days={activityDays}
                realDayNo={base.dayNo}
                currentStreak={base.currentStreak}
                bestStreak={base.bestStreak}
                onSelectDay={handleSelectDay}
                isTodayActive={isTodayActive}
              />
              <BadgesSection badges={badges} />
              <RulesCard />
            </div>
            <aside className="c99h-r" aria-label="Your stats">
              <RankCard myRank={leaderboard.myRank?.rank} totalParticipants={leaderboard.totalParticipants} />
              <StreakCard
                currentStreak={base.currentStreak}
                bestStreak={base.bestStreak}
                realDayNo={base.dayNo}
                activityByDay={activityByDay}
                myRank={leaderboard.myRank?.rank}
                totalParticipants={leaderboard.totalParticipants}
                isHftUpcoming={base.hasStarted === false || base.tasks?.some((t) => t.state === 'upcoming')}
                isTodayActive={isTodayActive}
              />
              <MiniLeaderboard
                leaderboard={viewedLeaderboard.leaderboard || []}
                myRank={viewedLeaderboard.myRank}
                weekNo={viewedWeekNo}
                currentWeekNo={currentWeekNo}
                loading={leaderboardLoading}
                totalParticipants={viewedLeaderboard.totalParticipants}
                me={me}
                onOpenFull={() => setShowFullLeaderboard(true)}
                onSelectWeek={handleSelectLeaderboardWeek}
              />
            </aside>
          </div>
          {showFullLeaderboard && (
            <LeaderboardDrawer
              leaderboard={viewedLeaderboard.leaderboard || []}
              myRank={viewedLeaderboard.myRank}
              me={me}
              weekNo={viewedWeekNo}
              totalParticipants={viewedLeaderboard.totalParticipants}
              onClose={() => setShowFullLeaderboard(false)}
            />
          )}
        </>
      )}
    </AppLayout>
  );
}
